import { Link } from "react-router";
import { Compass } from "lucide-react";

import { useTranslation } from "@/i18n/use-translation";

/**
 * "Explore this" — the hand-off from a dashboard panel to the free canvas (POS2-886).
 *
 * Bottom-right of the panel, on its own row. Not the hover pill — that holds layout controls,
 * which are occasional and fine to hide, while this is a primary way through the product and
 * has to be findable without hovering. Not the header either: panels carry their own header
 * actions (a total, a toggle), so the link landed in a different place on every card. One
 * fixed corner reads as "you have read this, now go dig". Labelled, not a bare icon.
 *
 * It renders its own row, so a panel with nothing to hand over leaves no empty spacer behind:
 * `href` is null when the window has no concrete bounds (all time) or the panel shows a
 * dimension Explore cannot split by. Every viewer sees it otherwise — the platform-admin
 * check that used to sit here went with the launch (POS2-1046).
 */
export function ExploreThisLink({ href, label }: { href: string | null; label: string }) {
  const { t } = useTranslation();
  if (!href) return null;
  return (
    <div className="mt-3 flex justify-end">
      <Link
        to={href}
        className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-gray-400 outline-none transition-colors hover:bg-blue-50 hover:text-blue-600 focus-visible:ring-1 focus-visible:ring-blue-500"
        aria-label={t("dashboard.exploreThisLabel", { label })}
        data-testid="explore-this-link"
      >
        <Compass className="size-3.5" aria-hidden />
        {t("dashboard.exploreThis")}
      </Link>
    </div>
  );
}
